import React, { useEffect, useState } from "react";
import { Loader2, Activity, AlertTriangle } from "lucide-react";
import GEXChart from "@/components/GEXChart";
import { fetchGEX } from "@/lib/api";

/**
 * AnalysisGEXCard — embeds the GEX chart for a single symbol on the Analysis page.
 *
 * Tries the real GEX surface from yfinance options chains. For tickers without
 * a liquid options chain (rare) we surface a clear "no chain" state.
 */
export const AnalysisGEXCard = ({ symbol }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (!symbol) return;
    let cancelled = false;
    setLoading(true);
    setErr(null);
    setData(null);
    fetchGEX(symbol)
      .then((d) => { if (!cancelled) setData(d); })
      .catch((e) => {
        if (!cancelled) {
          const detail = e?.response?.data?.detail || "Could not load GEX";
          setErr(detail);
        }
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [symbol]);

  return (
    <div
      className="rounded-xl border border-white/10 bg-[#070710] p-4"
      data-testid="analysis-gex-card"
    >
      <div className="flex items-center justify-between gap-2 mb-2">
        <div className="flex items-center gap-2">
          <Activity className="w-4 h-4 text-[#3FE5DC]" />
          <span className="text-[10px] uppercase tracking-[0.22em] text-[#3FE5DC]">
            Gamma exposure · {symbol}
          </span>
        </div>
        {data && (
          <span className="text-[10px] uppercase tracking-[0.18em] text-white/35 num">
            spot ${data.spot} · flip ${data.gamma_flip} · cw ${data.call_wall} · pw ${data.put_wall}
          </span>
        )}
      </div>

      {err && (
        <div
          className="rounded-md border border-[#FF3B30]/30 bg-[#FF3B30]/5 px-3 py-2 text-[11.5px] text-[#FF3B30] flex items-center gap-2"
          data-testid="analysis-gex-error"
        >
          <AlertTriangle className="w-3.5 h-3.5 shrink-0" />
          <span>{err} — try a more liquid ticker (SPY/QQQ/NVDA).</span>
        </div>
      )}

      {!err && (
        <div className="-mx-1">
          {loading && (
            <div className="flex items-center gap-2 text-white/55 text-xs py-2">
              <Loader2 className="w-3 h-3 animate-spin" /> Loading gamma surface…
            </div>
          )}
          <GEXChart data={data} loading={loading} />
        </div>
      )}
    </div>
  );
};
